import { useSelector } from "react-redux";
import { useEffect, useState } from "react";

import { RootState } from "../store";
import { BillDocumentType } from "../interfaces/BillInterfaces";
import useCurrentResourceCentre from "./useCurrentResourceCentre";
import { paymentOptionsEnum } from "../containers/Billing/Editor/BillSummary";
import {
  IFonepayDynamicQrData,
  INepalPayDynamicQrData,
  TQrPaymentInfo
} from "../interfaces/QrPayment";
import { NepalPayFields } from "../containers/ResourceCentre/Settings/PaymentSettings/NepalPayForm";
import { FonepayFields } from "../containers/ResourceCentre/Settings/PaymentSettings/FonepayForm";

const isQrSupported = (key: string): boolean =>
  ["esewa", "khalti", "fonepay", "bank", "nepalpay"].includes(key);

interface UseCanPayWithQrReturn {
  canPayWithNepalPayDynamicQr: boolean;
  canPayWithFonepayDynamicQr: boolean;

  canPayWithEsewaQr: boolean;
  esewaQrS3Url: string | null;
  esewaQrString: string | null;

  canPayWithKhaltiQr: boolean;
  khaltiQrS3Url: string | null;
  khaltiQrString: string | null;

  canPayWithFonepayQr: boolean;
  fonepayQrS3Url: string | null;

  canPayWithBankQr: boolean;
  bankQrS3Url: string | null;

  canPayWithStaticQr: boolean;
  paymentMethodToQrMapper: (method: keyof TQrPaymentInfo | null) => {
    qrString: string | null;
    qrS3Url: string | null;
  };
}

const useCanPayWithQr = (): UseCanPayWithQrReturn => {
  const rc = useCurrentResourceCentre();
  const dynamicQrMetadata = rc?.dynamicQrPaymentInfoMetadata ?? {};

  // selector
  const draft: BillDocumentType = useSelector((state: RootState) => state.bills.draft);

  // local states
  const [canPayWithFonepayDynamicQr, setCanPayWithFonepayDynamicQr] = useState(false);
  const [canPayWithNepalPayDynamicQr, setCanPayWithNepalPayDynamicQr] = useState(false);

  const [canPayWithEsewaQr, setCanPayWithEsewaQr] = useState(false);
  const [esewaQrS3Url, setEsewaQrS3Url] = useState<string | null>(null);
  const [esewaQrString, setEsewaQrString] = useState<string | null>(null);

  const [canPayWithKhaltiQr, setCanPayWithKhaltiQr] = useState(false);
  const [khaltiQrS3Url, setKhaltiQrS3Url] = useState<string | null>(null);
  const [khaltiQrString, setKhaltiQrString] = useState<string | null>(null);

  const [canPayWithFonepayQr, setCanPayWithFonepayQr] = useState(false);
  const [fonepayQrS3Url, setFonepayQrS3Url] = useState<string | null>(null);

  const [canPayWithBankQr, setCanPayWithBankQr] = useState(false);
  const [bankQrS3Url, setBankQrS3Url] = useState<string | null>(null);

  // variables
  const nepalPayDynamicQrPaymentInfo: INepalPayDynamicQrData | undefined =
    rc.dynamicQrPaymentInfo?.nepalPay ?? undefined;

  const fonepayDynamicQrPaymentInfo: IFonepayDynamicQrData | undefined =
    rc.dynamicQrPaymentInfo?.fonepay ?? undefined;

  // helper functions
  const reset = () => {
    setCanPayWithNepalPayDynamicQr(false);
    setCanPayWithFonepayDynamicQr(false);

    setCanPayWithEsewaQr(false);
    setEsewaQrS3Url(null);
    setEsewaQrString(null);

    setCanPayWithKhaltiQr(false);
    setKhaltiQrS3Url(null);
    setKhaltiQrString(null);

    setCanPayWithFonepayQr(false);
    setFonepayQrS3Url(null);

    setCanPayWithBankQr(false);
    setBankQrS3Url(null);
  };

  const paymentMethodToQrMapper = (
    method: keyof TQrPaymentInfo | null
  ): { qrString: string | null; qrS3Url: string | null } => {
    switch (method) {
      case "esewa":
        return {
          qrS3Url: esewaQrS3Url,
          qrString: esewaQrString
        };

      case "khalti":
        return {
          qrS3Url: khaltiQrS3Url,
          qrString: khaltiQrString
        };

      case "fonepay":
        return {
          qrS3Url: fonepayQrS3Url,
          qrString: null
        };

      case "bank":
        return {
          qrS3Url: bankQrS3Url,
          qrString: null
        };

      default:
        return {
          qrS3Url: null,
          qrString: null
        };
    }
  };

  // effect
  useEffect(() => {
    reset();
    if (!rc?.id) {
      return;
    }

    const breakdown = draft?.paymentInfo?.paymentBreakdown || [];
    if (breakdown.length === 0) {
      reset();
      return;
    }

    breakdown.forEach(({ method }) => {
      const draftMethod =
        method.toLowerCase() === paymentOptionsEnum.bankTransfer.toLowerCase()
          ? "bank"
          : method?.toLowerCase();

      if (!isQrSupported(draftMethod)) return;

      if (draftMethod === paymentOptionsEnum.nepalPay.toLowerCase()) {
        if (!nepalPayDynamicQrPaymentInfo) return;

        const allRequiredFilled = NepalPayFields.filter((f) => f.required).every(
          (f) =>
            nepalPayDynamicQrPaymentInfo[f.name] !== null &&
            nepalPayDynamicQrPaymentInfo[f.name] !== undefined
        );

        if (
          allRequiredFilled &&
          dynamicQrMetadata?.nepalPay?.hasPassword &&
          dynamicQrMetadata?.nepalPay?.hasPrivateKey
        )
          setCanPayWithNepalPayDynamicQr(true);
      } else if (draftMethod === paymentOptionsEnum.fonePay.toLocaleLowerCase()) {
        if (!fonepayDynamicQrPaymentInfo) return;

        const allRequiredFilled = FonepayFields.filter((f) => f.required).every(
          (f) =>
            fonepayDynamicQrPaymentInfo[f.name] !== null &&
            fonepayDynamicQrPaymentInfo[f.name] !== undefined
        );

        if (
          allRequiredFilled &&
          dynamicQrMetadata?.fonepay?.hasPassword &&
          dynamicQrMetadata?.fonepay?.hasSecretKey
        )
          setCanPayWithFonepayDynamicQr(true);
      } else {
        const qrPaymentInfo = rc.qrPaymentInfo as TQrPaymentInfo;
        const qrData = qrPaymentInfo[draftMethod as keyof TQrPaymentInfo];

        if (!qrData) return;

        const qrImage = qrData?.images?.[0];
        const qrCred = qrData.credentials?.[0];
        const hasImage = qrImage?.url && qrImage?.key;
        const hasCred = qrCred?.id && qrCred?.name;

        switch (draftMethod) {
          case "esewa": {
            if (hasImage) {
              setCanPayWithEsewaQr(true);
              setEsewaQrS3Url(qrImage.url);
            } else if (hasCred) {
              setCanPayWithEsewaQr(true);
              setEsewaQrString(JSON.stringify({ eSewa_id: qrCred.id, name: qrCred.name }));
            }

            break;
          }

          case "khalti": {
            if (hasImage) {
              setCanPayWithKhaltiQr(true);
              setKhaltiQrS3Url(qrImage.url);
            } else if (hasCred) {
              setCanPayWithKhaltiQr(true);
              setKhaltiQrString(JSON.stringify({ Khalti_ID: qrCred.id, name: qrCred.name }));
            }

            break;
          }

          case "fonepay": {
            if (hasImage) {
              setCanPayWithFonepayQr(true);
              setFonepayQrS3Url(qrImage.url);
            }

            break;
          }

          case "bank": {
            if (hasImage) {
              setCanPayWithBankQr(true);
              setBankQrS3Url(qrImage.url);
            }

            break;
          }

          default:
            break;
        }
      }
    });
  }, [
    draft?.paymentInfo?.paymentBreakdown,
    rc?.id,
    rc.qrPaymentInfo,
    rc.dynamicQrPaymentInfo,
    nepalPayDynamicQrPaymentInfo,
    dynamicQrMetadata?.nepalPay?.hasPassword,
    dynamicQrMetadata?.nepalPay?.hasPrivateKey,
    dynamicQrMetadata?.fonepay?.hasPassword,
    dynamicQrMetadata?.fonepay?.hasSecretKey,
    fonepayDynamicQrPaymentInfo
  ]);

  const canPayWithStaticQr =
    canPayWithEsewaQr || canPayWithKhaltiQr || canPayWithFonepayQr || canPayWithBankQr;

  return {
    canPayWithNepalPayDynamicQr,
    canPayWithFonepayDynamicQr,

    canPayWithEsewaQr,
    esewaQrS3Url,
    esewaQrString,

    canPayWithKhaltiQr,
    khaltiQrS3Url,
    khaltiQrString,

    canPayWithFonepayQr,
    fonepayQrS3Url,

    canPayWithBankQr,
    bankQrS3Url,

    canPayWithStaticQr,
    paymentMethodToQrMapper
  };
};

export default useCanPayWithQr;
